import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import OCRUploadDialog from "@/components/OCRUploadDialog";
import { PresenceStrip } from "@/components/PresenceStrip";
import { toast } from "sonner";
import { Receipt, Plus, CheckCircle2, CircleDollarSign, XCircle, Sparkles, Trash2, ScanLine, Wand2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CATEGORIES = [
  "utilities", "landscaping", "insurance", "management", "repairs",
  "cleaning", "security", "legal", "reserves", "other",
];

const STATUS_STYLE = {
  draft:    "border-zinc-500/40 text-zinc-300 bg-zinc-500/10",
  pending:  "border-amber-500/40 text-amber-300 bg-amber-500/10",
  approved: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  paid:     "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  void:     "border-red-500/40 text-red-300 bg-red-500/10",
};

export default function APPage() {
  const { currentProperty } = useProperty();
  const [bills, setBills] = useState({ items: [], totals: {} });
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [newBillOpen, setNewBillOpen] = useState(false);
  const [bulkRecat, setBulkRecat] = useState(null);
  const [bulkRecatBusy, setBulkRecatBusy] = useState(false);

  const load = useCallback(async () => {
    if (!currentProperty?.id) return;
    setLoading(true);
    try {
      const params = { property_id: currentProperty.id, limit: 500 };
      if (status !== "all") params.status = status;
      const { data } = await api.get("/admin/ap/bills", { params });
      setBills(data);
      setSelected(new Set());
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setLoading(false);
  }, [currentProperty?.id, status]);

  useEffect(() => { load(); }, [load]);

  const totals = bills.totals || {};

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selected.size === bills.items.length) setSelected(new Set());
    else setSelected(new Set(bills.items.map((b) => b.id)));
  };

  const canBulkApprove = useMemo(() => {
    return bills.items.filter((b) => selected.has(b.id) && (b.status === "draft" || b.status === "pending")).length;
  }, [selected, bills.items]);
  const canBulkPay = useMemo(() => {
    return bills.items.filter((b) => selected.has(b.id) && b.status !== "paid" && b.status !== "void").length;
  }, [selected, bills.items]);

  const bulkApprove = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/ap/bills/bulk-approve", { bill_ids: ids });
      toast.success(`Approved ${data.approved.length}${data.skipped.length ? ` (skipped ${data.skipped.length})` : ""}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBulkBusy(false);
  };
  const bulkPay = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/ap/bills/bulk-mark-paid", { bill_ids: ids });
      toast.success(`Marked ${data.paid.length} paid${data.skipped.length ? ` (skipped ${data.skipped.length})` : ""}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBulkBusy(false);
  };

  const runBulkRecat = async () => {
    if (!currentProperty?.id) return toast.error("Pick a property first");
    setBulkRecatBusy(true);
    setBulkRecat(null);
    try {
      const { data } = await api.post("/ai/bulk-categorize-expenses", {
        property_id: currentProperty.id,
        only_missing: true,
        limit: 50,
      });
      setBulkRecat(data);
      toast.success(`Recategorized ${data.updated}/${data.processed} expenses`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBulkRecatBusy(false);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Financials"
        title="Accounts Payable"
        subtitle="Vendor bills — draft, approve, and pay. Scan an invoice image to auto-fill fields."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <PresenceStrip propertyId={currentProperty?.id} testId="ap-presence" />
            <Button
              variant="outline" className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10"
              onClick={runBulkRecat} disabled={bulkRecatBusy} data-testid="ap-bulk-recat-btn"
              title="AI-recategorize expenses currently marked 'other'"
            >
              <Wand2 className="h-4 w-4 mr-2" /> {bulkRecatBusy ? "Sweeping…" : "AI recategorize"}
            </Button>
            <OCRUploadDialog onExtracted={(fields) => { setPrefill(fields); setNewBillOpen(true); }} />
            <NewBillDialog open={newBillOpen} setOpen={setNewBillOpen} prefill={prefill} onOpenChange={(v) => { if (!v) setPrefill(null); }} onCreated={load} propertyId={currentProperty?.id} />
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard label="Pending" value={money(totals.pending_amount || 0)} testId="ap-stat-pending" tone="warn" />
        <StatCard label="Approved" value={money(totals.approved_amount || 0)} testId="ap-stat-approved" tone="info" />
        <StatCard label="Paid" value={money(totals.paid_amount || 0)} testId="ap-stat-paid" tone="good" />
        <StatCard label="Bills on file" value={totals.count || 0} testId="ap-stat-count" tone="default" />
      </div>

      {bulkRecat && (
        <Card className="border-indigo-500/30 bg-indigo-500/[0.03] p-3 mb-4 flex items-center gap-3" data-testid="ap-recat-result">
          <Wand2 className="h-4 w-4 text-indigo-400 shrink-0" />
          <div className="flex-1 text-sm">
            AI sweep — processed {bulkRecat.processed}, updated {bulkRecat.updated}. Rerun to expand.
          </div>
          <Button size="sm" variant="ghost" onClick={() => setBulkRecat(null)}>Dismiss</Button>
        </Card>
      )}

      <ARSummaryCardEmbed />

      <Card className="border-white/10 bg-card p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Label className="text-xs">Filter by status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="ap-filter-status" className="w-56 bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All bills</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending">Pending approval</SelectItem>
              <SelectItem value="approved">Approved — ready to pay</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="void">Void</SelectItem>
            </SelectContent>
          </Select>
          {selected.size > 0 && (
            <div className="ml-auto flex items-center gap-2" data-testid="ap-bulk-bar">
              <span className="text-xs text-zinc-400">{selected.size} selected</span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 h-8" disabled={bulkBusy || canBulkApprove === 0} data-testid="ap-bulk-approve">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Approve ({canBulkApprove})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="ap-bulk-approve-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Approve {canBulkApprove} bill{canBulkApprove === 1 ? "" : "s"}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This routes them to your board (or straight to Mark-Paid). Non-eligible bills in the selection are silently skipped. All approvals are audit-logged and fire the <code>bill.approved</code> webhook.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="ap-bulk-approve-cancel">Not yet</AlertDialogCancel>
                    <AlertDialogAction onClick={bulkApprove} className="bg-indigo-500 hover:bg-indigo-600" data-testid="ap-bulk-approve-yes">Approve {canBulkApprove}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 h-8" disabled={bulkBusy || canBulkPay === 0} data-testid="ap-bulk-pay">
                    <CircleDollarSign className="h-3 w-3 mr-1" /> Mark paid ({canBulkPay})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="ap-bulk-pay-confirm">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Mark {canBulkPay} bill{canBulkPay === 1 ? "" : "s"} paid?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Records that money left the account. Reflected in the General Ledger and fires the <code>bill.paid</code> webhook. This can&apos;t be undone without voiding.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="ap-bulk-pay-cancel">Not yet</AlertDialogCancel>
                    <AlertDialogAction onClick={bulkPay} className="bg-emerald-500 hover:bg-emerald-600" data-testid="ap-bulk-pay-yes">Mark {canBulkPay} paid</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="ap-bulk-clear">Clear</Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="border-white/10 bg-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ap-bills-table">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="p-3 w-8">
                  <Checkbox
                    checked={bills.items.length > 0 && selected.size === bills.items.length}
                    onCheckedChange={toggleAll}
                    data-testid="ap-select-all"
                    aria-label="Select all"
                  />
                </th>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Vendor</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3 hidden md:table-cell">Memo</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {bills.items.map((b) => (
                <BillRow key={b.id} bill={b} onChanged={load} checked={selected.has(b.id)} onToggle={() => toggleOne(b.id)} />
              ))}
              {bills.items.length === 0 && !loading && (
                <tr><td colSpan={8} className="p-8 text-center text-zinc-500 text-sm">No bills yet. Scan an invoice or click &ldquo;New bill&rdquo; above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StatCard({ label, value, testId, tone }) {
  const toneStyle = {
    good: "border-emerald-500/20 bg-emerald-500/[0.03]",
    warn: "border-amber-500/20 bg-amber-500/[0.03]",
    info: "border-indigo-500/20 bg-indigo-500/[0.03]",
    default: "border-white/10 bg-card",
  }[tone];
  return (
    <Card className={"p-4 " + toneStyle} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-xl font-semibold font-display mt-1">{value}</div>
    </Card>
  );
}

function BillRow({ bill, onChanged, checked, onToggle }) {
  const [busy, setBusy] = useState(false);
  const act = async (path, verb) => {
    setBusy(true);
    try { await api.post(`/admin/ap/bills/${bill.id}/${path}`); toast.success(`Bill ${verb}`); onChanged?.(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };
  const del = async () => {
    setBusy(true);
    try { await api.delete(`/admin/ap/bills/${bill.id}`); toast.success("Bill deleted"); onChanged?.(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };
  return (
    <tr className="hover:bg-white/[0.02]" data-testid={`ap-row-${bill.id}`}>
      <td className="p-3">
        <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`ap-select-${bill.id}`} aria-label="Select bill" />
      </td>
      <td className="p-3 whitespace-nowrap text-zinc-400">{bill.date}</td>
      <td className="p-3">{bill.vendor}</td>
      <td className="p-3 text-zinc-400">{bill.category}</td>
      <td className="p-3 hidden md:table-cell text-zinc-400 truncate max-w-[280px]" title={bill.memo}>{bill.memo || "—"}</td>
      <td className="p-3 text-right font-mono">{money(bill.amount)}</td>
      <td className="p-3 text-center">
        <Badge variant="outline" className={STATUS_STYLE[bill.status] || "border-white/20"} data-testid={`ap-status-${bill.id}`}>{bill.status}</Badge>
      </td>
      <td className="p-3 text-right">
        <div className="inline-flex gap-1">
          {(bill.status === "draft" || bill.status === "pending") && (
            <Button size="sm" variant="outline" className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10 h-7" onClick={() => act("approve", "approved")} disabled={busy} data-testid={`ap-approve-${bill.id}`}>
              <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
            </Button>
          )}
          {(bill.status === "approved" || bill.status === "pending" || bill.status === "draft") && (
            <Button size="sm" variant="outline" className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 h-7" onClick={() => act("mark-paid", "marked paid")} disabled={busy} data-testid={`ap-pay-${bill.id}`}>
              <CircleDollarSign className="h-3 w-3 mr-1" /> Mark paid
            </Button>
          )}
          {bill.status !== "paid" && bill.status !== "void" && (
            <Button size="sm" variant="outline" className="border-red-500/30 text-red-300 hover:bg-red-500/10 h-7" onClick={() => act("void", "voided")} disabled={busy} data-testid={`ap-void-${bill.id}`}>
              <XCircle className="h-3 w-3 mr-1" /> Void
            </Button>
          )}
          {bill.status !== "paid" && (
            <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-300 h-7" onClick={del} disabled={busy} data-testid={`ap-delete-${bill.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}

function NewBillDialog({ open, setOpen, onCreated, propertyId, prefill, onOpenChange }) {
  const [form, setForm] = useState({
    vendor: "", invoice_number: "",
    date: new Date().toISOString().slice(0, 10),
    due_date: "", amount: "", category: "other", memo: "",
    status: "pending",
  });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  useEffect(() => {
    if (open) {
      if (prefill) {
        setForm({
          vendor: prefill.vendor || "",
          invoice_number: prefill.invoice_number || "",
          date: prefill.date || new Date().toISOString().slice(0, 10),
          due_date: prefill.due_date || "",
          amount: String(prefill.amount || ""),
          category: prefill.category || "other",
          memo: prefill.memo || "",
          status: "pending",
        });
      } else {
        setForm({
          vendor: "", invoice_number: "",
          date: new Date().toISOString().slice(0, 10),
          due_date: "", amount: "", category: "other", memo: "",
          status: "pending",
        });
      }
    }
  }, [open, prefill]);

  const suggestCategory = async () => {
    if (!form.vendor.trim() && !form.memo.trim()) return toast.error("Vendor or memo required");
    setSuggesting(true);
    try {
      const { data } = await api.post("/ai/categorize-expense", {
        vendor: form.vendor, memo: form.memo, amount: parseFloat(form.amount) || null,
      });
      setForm((prev) => ({ ...prev, category: data.category }));
      toast.success(`Suggested: ${data.category} (${Math.round((data.confidence || 0) * 100)}% confidence)`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSuggesting(false);
  };

  const save = async () => {
    if (!propertyId) return toast.error("Pick a property first");
    const n = parseFloat(form.amount);
    if (!n || n <= 0) return toast.error("Enter an amount > 0");
    if (!form.vendor.trim()) return toast.error("Vendor is required");
    setSaving(true);
    try {
      await api.post("/admin/ap/bills", {
        property_id: propertyId,
        vendor: form.vendor.trim(),
        invoice_number: form.invoice_number.trim() || null,
        date: form.date,
        due_date: form.due_date || null,
        amount: n,
        category: form.category,
        memo: form.memo.trim() || null,
        status: form.status,
      });
      toast.success(`Bill created (${form.status})`);
      setOpen(false);
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); onOpenChange?.(v); }}>
      <DialogTrigger asChild>
        <Button data-testid="ap-new-bill-btn" className="bg-orange-500 hover:bg-orange-600">
          <Plus className="h-4 w-4 mr-2" /> New bill
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="ap-new-bill-dialog">
        <DialogHeader>
          <DialogTitle><Receipt className="h-4 w-4 inline mr-1" /> {prefill ? "New bill (from OCR)" : "New vendor bill"}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-400">
            {prefill ? "Fields prefilled from the scanned invoice — review then save." : "Save as draft or send for approval. Approved bills can be marked paid."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Vendor *</Label>
            <Input data-testid="ap-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="Acme HVAC" />
          </div>
          <div>
            <Label>Invoice #</Label>
            <Input data-testid="ap-invoice-no" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Optional" />
          </div>
          <div>
            <Label>Amount *</Label>
            <Input data-testid="ap-amount" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <Label>Bill date</Label>
            <Input data-testid="ap-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <Label>Due date</Label>
            <Input data-testid="ap-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div className="col-span-2">
            <div className="flex items-center justify-between">
              <Label>Category</Label>
              <Button size="sm" variant="ghost" className="h-6 text-[10px] uppercase tracking-widest text-indigo-300 hover:text-indigo-200 hover:bg-indigo-500/10" onClick={suggestCategory} disabled={suggesting} data-testid="ap-ai-categorize">
                <Sparkles className="h-3 w-3 mr-1" /> {suggesting ? "Thinking…" : "AI suggest"}
              </Button>
            </div>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="ap-category" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Memo</Label>
            <Textarea data-testid="ap-memo" rows={2} maxLength={200} value={form.memo} onChange={(e) => setForm({ ...form, memo: e.target.value })} placeholder="Emergency chiller repair, unit 3B" />
          </div>
          <div className="col-span-2">
            <Label>Initial status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="ap-status" className="bg-white/[0.02] border-white/10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft (not routed yet)</SelectItem>
                <SelectItem value="pending">Pending approval</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 mt-3" data-testid="ap-save">
          {saving ? "Saving…" : "Save bill"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// Re-exported AR summary that reads the same endpoint. Kept as an internal
// helper so we can wrap it in a hidden wrapper for isolated tests.
export function ARSummaryCard() { return <ARSummaryCardEmbed />; }

function ARSummaryCardEmbed() {
  const { currentProperty } = useProperty();
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      if (!currentProperty?.id) return;
      try {
        const { data } = await api.get("/admin/ar/summary", { params: { property_id: currentProperty.id } });
        setData(data);
      } catch { setData(null); }
    })();
  }, [currentProperty?.id]);
  if (!data) return null;
  const bucketOrder = ["Current", "1-30", "31-60", "61-90", "90+"];
  const bucketColors = {
    "Current": "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
    "1-30":    "bg-amber-500/10 border-amber-500/30 text-amber-300",
    "31-60":   "bg-orange-500/10 border-orange-500/30 text-orange-300",
    "61-90":   "bg-red-500/10 border-red-500/30 text-red-300",
    "90+":     "bg-red-600/20 border-red-600/40 text-red-200",
  };
  return (
    <Card className="border-white/10 bg-card p-5 mb-4" data-testid="ar-summary-card">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline flex items-center gap-1"><Receipt className="h-3 w-3" /> Accounts Receivable</div>
          <div className="text-2xl font-semibold font-display mt-1">{money(data.total_ar)}</div>
          <div className="text-xs text-zinc-500 mt-1">{data.residents_with_balance} residents with a balance</div>
        </div>
      </div>
      <div className="grid grid-cols-5 gap-1.5 mb-4">
        {bucketOrder.map((b) => (
          <div key={b} className={"rounded-md border p-2 text-center " + bucketColors[b]} data-testid={`ar-bucket-${b}`}>
            <div className="text-[9px] uppercase tracking-widest opacity-70">{b}</div>
            <div className="text-sm font-mono">{money(data.buckets[b] || 0)}</div>
          </div>
        ))}
      </div>
      {data.top_delinquents.length > 0 && (
        <>
          <div className="overline mb-2">Top delinquents</div>
          <div className="space-y-1.5">
            {data.top_delinquents.slice(0, 5).map((r) => (
              <div key={r.resident_id} className="flex items-center justify-between text-sm rounded-md bg-white/[0.02] border border-white/5 px-3 py-2" data-testid={`ar-delinquent-${r.resident_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{r.name || r.email}</div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {r.unit ? `Unit ${r.unit} · ` : ""}{r.oldest_days_late} days late · <span className={bucketColors[r.bucket]}>{r.bucket}</span>
                  </div>
                </div>
                <div className="font-mono text-red-300">{money(r.balance)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
